/**
 * PAUSED-record terminal transitions for inbound peer `cancel`/`error` ctrls
 * (docs/file-transfer-resume.md §9.1/§9.2 PAUSED rows). Web's mirror of
 * `apps/extension/src/background/pausedTransferTerminal.ts` — on web there is
 * no separate "idle-closed offscreen host" context to worry about (the page
 * IS the only context), but a paused record still has no LIVE LOOP driving
 * it, so this module owns exactly that no-loop-running subset — the same
 * role the extension's SW plays, just running inline here.
 *
 * Semantics mirror the resume coordinators' terminal machinery verbatim,
 * preserving the §8.2 asymmetries (see the extension module's table:
 * sender-cancel DELETEs + never echoes a ctrl; sender-error never DELETEs).
 * Only the SENDER can reach `"paused"` — the receiver-pause primitive was cut,
 * so a receiver never pauses and there is no receiver arm here.
 *
 * SECURITY: never log file name/content/manifest fields — security.md.
 */

import { deleteOpfsTransferFile } from "@reflecto/client-core";
import type { FileCtrl } from "@reflecto/core";
import { webStorage } from "../adapters/storage.web";
import { deleteTransfer as apiDeleteTransfer } from "../api/client";
import { getSenderState, saveSenderState } from "./senderState";

async function tryLock(transferId: string, fn: () => Promise<void>): Promise<void> {
  const locks = (globalThis.navigator as Navigator | undefined)?.locks;
  if (!locks) {
    await fn();
    return;
  }
  await locks.request(`reflecto-ft-${transferId}`, { ifAvailable: true }, async (lock) => {
    if (!lock) return; // another context is driving this transfer — its live-loop routing owns the ctrl
    await fn();
  });
}

/**
 * Terminal-transition any PAUSED sender/receiver record targeted by an
 * inbound peer `cancel`/`error` ctrl. Any other kind, any other status
 * (active records are the live-loop gates' job; terminal tombstones drop),
 * or no record at all → no-op.
 */
export async function terminatePausedOnPeerCtrl(ctrl: FileCtrl): Promise<void> {
  if (ctrl.kind !== "cancel" && ctrl.kind !== "error") return;
  const { transferId } = ctrl;
  const verdict =
    ctrl.kind === "cancel"
      ? ({ status: "cancelled", errorCode: undefined } as const)
      : ({ status: "failed", errorCode: ctrl.code } as const);

  await tryLock(transferId, async () => {
    // Re-read INSIDE the lock — a resume that just released it may have
    // already driven the record to a different status.
    const sender = await getSenderState(transferId);
    if (sender?.status === "paused") {
      if (ctrl.kind === "cancel") {
        const { token } = await webStorage.get("token");
        if (token) await apiDeleteTransfer(token, transferId).catch(() => {});
      }
      sender.status = verdict.status;
      sender.errorCode = verdict.errorCode;
      sender.updatedAt = Date.now();
      await saveSenderState(sender); // P-S4
      await deleteOpfsTransferFile(`send-${transferId}`).catch(() => {});
      return; // a transferId is one-directional — no receiver record can also match
    }
    // Receiver records never reach "paused" (the receiver-pause primitive was
    // cut), so there is no receiver arm here — an inbound peer cancel/error for
    // a receive is handled only by the live-loop `aborted` flag, never here.
  });
}
